import React, { useState, useEffect, useRef } from 'react';
import ApiService from '../services/api';
import './AddClubModal.css';

interface Club {
  id: number;
  uuid?: string;
  name: string;
  location?: string;
  venue?: string;
  activity_type: string;
  is_private?: boolean;
  kit_items?: string[];
  start_time?: string;
  end_time?: string;
}

interface Child {
  uuid: string;
  name: string;
  display_name?: string;
  school_venue?: string;
  school_city?: string;
}

interface TermPlannerClubModalProps {
  onClubSelect: (club: Club) => void;
  onCancel: () => void;
  child?: Child;
}

const TermPlannerClubModal: React.FC<TermPlannerClubModalProps> = ({ onClubSelect, onCancel, child }) => {
  const [view, setView] = useState<'browse' | 'create'>('browse');
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Create form state
  const [clubName, setClubName] = useState('');
  const [location, setLocation] = useState(child?.school_city || '');
  const [venue, setVenue] = useState(child?.school_venue || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const clubNameInputRef = useRef<HTMLInputElement>(null);

  const apiService = ApiService.getInstance();

  useEffect(() => {
    if (view === 'browse') {
      loadClubs();
    } else if (view === 'create') {
      // Auto-focus club name field when switching to create view
      setTimeout(() => {
        clubNameInputRef.current?.focus();
      }, 100);
    }
  }, [view, searchTerm]);

  const loadClubs = async () => {
    try {
      setLoading(true);
      // Filter by "School Club" activity type
      const response = await apiService.getClubs('School Club', searchTerm, '');

      if (response.success) {
        const clubsData = response.data || [];
        setClubs(clubsData);
      }
    } catch (error) {
      console.error('Failed to load clubs:', error);
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!clubName.trim()) {
      newErrors.name = 'Club name is required';
    }

    // Location is optional
    // Venue is optional

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateClub = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const clubData = {
        name: clubName,
        location: location,
        venue: venue,
        activity_type: 'School Club',
        is_private: true,
        website_url: '',
        cost: '',
        max_participants: '',
        contact_phone: '',
        contact_email: ''
      };

      const response = await apiService.createClub(clubData);

      if (response.success && response.data) {
        // Return the newly created club
        onClubSelect({
          id: response.data.id,
          uuid: response.data.uuid,
          name: clubName,
          location: location,
          venue: venue,
          activity_type: 'School Club',
          is_private: true
        });
      } else {
        alert('Failed to create club');
      }
    } catch (error) {
      console.error('Failed to create club:', error);
      alert('Failed to create club');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectClub = (club: Club) => {
    onClubSelect(club);
  };

  return (
    <div className="add-club-modal-overlay" onClick={onCancel}>
      <div
        className="add-club-modal"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        style={{ maxHeight: '80vh' }}
      >
        <div className="add-club-modal-header">
          <h2>{view === 'browse' ? 'Select New Option' : 'Create New School Club'}</h2>
          <button onClick={onCancel} className="close-btn" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="add-club-modal-content">
          {view === 'browse' ? (
            <>
              {/* Create New Button - Moved to top */}
              <button
                onClick={() => setView('create')}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '15px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                + Create New School Club
              </button>

              {/* Search */}
              <div className="form-group">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search clubs by name..."
                  className="form-input"
                  style={{ marginBottom: '15px' }}
                />
              </div>

              {/* Club List */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>Loading clubs...</div>
              ) : clubs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  No school clubs found. {searchTerm && 'Try a different search.'}
                </div>
              ) : (
                <div>
                  {clubs.map((club) => (
                    <div
                      key={club.id}
                      onClick={() => handleSelectClub(club)}
                      style={{
                        padding: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '10px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fa')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{club.name}</div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {club.venue && <span>{club.venue}, </span>}
                        {club.location}
                      </div>
                      {club.kit_items && club.kit_items.length > 0 && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                          🎒 Kit: {club.kit_items.join(', ')}
                        </div>
                      )}
                      {club.is_private && (
                        <div style={{ fontSize: '12px', color: '#28a745', marginTop: '5px' }}>
                          🔒 Private Club
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Create Form */}
              <div className="form-group">
                <label htmlFor="club_name">Club Name *</label>
                <input
                  ref={clubNameInputRef}
                  id="club_name"
                  type="text"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                  placeholder="e.g., Breakfast Club"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              {/* Use School Name Button */}
              {child && child.school_venue && (
                <button
                  type="button"
                  onClick={() => {
                    setVenue(child.school_venue || '');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginBottom: '15px',
                    background: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  📍 Use School Name ({child.school_venue})
                </button>
              )}

              <div className="form-group">
                <label htmlFor="location">Town/City (Optional)</label>
                <input
                  id="location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., London"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="venue">Venue *</label>
                <input
                  id="venue"
                  type="text"
                  value={venue}
                  onChange={(e) => setVenue(e.target.value)}
                  placeholder="e.g., Main Hall"
                  className={`form-input ${errors.venue ? 'error' : ''}`}
                />
                {errors.venue && <span className="error-message">{errors.venue}</span>}
              </div>

              <div style={{ background: '#e3f2fd', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>
                <small>
                  <strong>Note:</strong> This club will be created as a private "School Club" activity type.
                </small>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => setView('browse')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={saving}
                >
                  Back
                </button>
                <button
                  onClick={handleCreateClub}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Club'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermPlannerClubModal;
